import { Invoice, PetInsuranceData } from "../models";

export function calculatePaidAmount(contract: PetInsuranceData, previousContact?: PetInsuranceData): {
  paidAmount: number,
  usedFranchise: number,
  contractStatus:  'expired' | 'active' | 'future'
} {
  let startYearDate
  let endYearDate
  let yearIndex = 0;
  let result: { paidAmount: number, usedFranchise: number, contractStatus: 'expired' | 'active' | 'future' } = {
    paidAmount: 0,
    usedFranchise: 0,
    contractStatus: 'expired'
  };

  let claims = contract.claims || [];

  if (previousContact) {
    // Starting on previousContract start date iterate through all years until less than year of previousContact end date
    let previousContractStartYear = new Date(previousContact.start_date);
    const previousContractEndYear = new Date(previousContact.end_date);
    while (previousContractStartYear.setFullYear(previousContractStartYear.getFullYear() + 1) < previousContractEndYear.getTime()) {
    }
    previousContractStartYear.setFullYear(previousContractStartYear.getFullYear() - 1);

    if (previousContact.claims) {
      claims = [...previousContact.claims.filter(claim => new Date(claim.claim_date) >= previousContractStartYear && new Date(claim.claim_date) <= previousContractEndYear), ...claims]
    }
  }

  result.contractStatus = new Date() > new Date(contract.end_date) ? 'expired' : (new Date() < new Date(contract.start_date) ? 'future' : 'active');

  if (contract.status === 'gekündigt' && contract.cancellation_date && new Date(contract.cancellation_date) <= new Date()) {
    result.contractStatus = 'expired';
  }

  if (!claims || claims.length === 0) {
    return result;
  }

  do {
    startYearDate = new Date(contract.start_date);
    startYearDate.setFullYear(startYearDate.getFullYear() + yearIndex);
    endYearDate = new Date(contract.start_date);
    endYearDate.setFullYear(endYearDate.getFullYear() + yearIndex + 1);
    endYearDate = new Date(endYearDate.getTime() - 1);
    let paidAmountTotal = 0;
    let usedFranchise = 0;

    for (let claim of claims) {
      if ((yearIndex === 0 || new Date(claim.claim_date) >= startYearDate) && new Date(claim.claim_date) < endYearDate) {
        usedFranchise += claim.deduction_yearly_franchise || 0;
        paidAmountTotal += claim.paid_amount || 0;
      }
    }
    result.paidAmount = paidAmountTotal;
    result.usedFranchise = usedFranchise;
    yearIndex++;
  } while (new Date() > endYearDate && endYearDate < new Date(contract.end_date));
  return result;
}

export function findPrevContract(allContracts: PetInsuranceData[], currentContract: PetInsuranceData) {
  const prevContracts = allContracts.filter(c=>c.base_number === currentContract.base_number && c.full_contract_number !== currentContract.full_contract_number && new Date(c.end_date) < new Date(currentContract.start_date));
  let prevContract;

  if (prevContracts.length > 0) {
    // Find latest
    const latest = prevContracts.reduce((prev, curr) => {
      return new Date(prev.end_date) > new Date(curr.end_date) ? prev : curr;
    });

    // Check if latest contract was end properly or if it was changed in middle of the year, if so was it changed right before current one
    const latestEnd = new Date(latest.end_date);
    const currentStart = new Date(currentContract.start_date);
    const diff = currentStart.getTime() - latestEnd.getTime();
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
    let previousContractStartYear = new Date(latest.start_date);
    const previousContractEndYear = new Date(latest.end_date);
    while (previousContractStartYear.setFullYear(previousContractStartYear.getFullYear() + 1) < previousContractEndYear.getTime()) {
    }
    previousContractStartYear.setFullYear(previousContractStartYear.getFullYear() - 1);
    const diffPrevContract = previousContractEndYear.getTime() - previousContractStartYear.getTime();
    const diffPrevContractDays = Math.ceil(diffPrevContract / (1000 * 3600 * 24));
    if (diffDays < 2 && diffPrevContractDays < (365 - 2) && latest.claims) {
      prevContract = latest;
    }
  }

  return prevContract;
}

export function generateYearsToDisplay(): { value: number, label: string }[] {
  //generated years from 2020 to 1900
  let result: { value: number, label: string }[] = [];
  for (let i = 2020; i >= 1900; i--) {
    result.push({
      value: i,
      label: i.toString()
    });
  }

  return result;
}

export function generateMonthsToDisplay(): { value: number, label: string }[] {
  return [
    { value: 1, label: 'common.month.1' },
    { value: 2, label: 'common.month.2' },
    { value: 3, label: 'common.month.3' },
    { value: 4, label: 'common.month.4' },
    { value: 5, label: 'common.month.5' },
    { value: 6, label: 'common.month.6' },
    { value: 7, label: 'common.month.7' },
    { value: 8, label: 'common.month.8' },
    { value: 9, label: 'common.month.9' },
    { value: 10, label: 'common.month.10' },
    { value: 11, label: 'common.month.11' },
    { value: 12, label: 'common.month.12' }
  ];
}

export function generateDaysForMonthToDisplay(year: number, month: number): { value: number, label: string }[] {
  const lastDayOfMonth = new Date(year, month, 0).getDate();

  const days: { value: number, label: string }[] = [];

  for (let day = 1; day <= lastDayOfMonth; day++) {
    const dayStr = day.toString();

    days.push({
      value: day,
      label: dayStr
    });
  }

  return days;
}

/**
 * Cognito stores the email exactly as it is sent, and a pre sign-up trigger cannot
 * change it, so every email we hand to Cognito goes through here first.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function hashFields(dataToHash: any): string {
  const jsonString = JSON.stringify(dataToHash);

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString(16);
}

export const recipientIbanFormated = 'CH24 3070 0110 0067 7127 2';
export const recipientName = 'Simpego Versicherungen AG';
export const recipientStreet = 'Hohlstrasse';
export const recipientHouseNumber = '556';
export const recipientZIP = '8048';
export const recipientCity = 'Zurich';
export const recipientCountry = 'CH';

/**
 * The invoice PDF endpoint answers 403 both for a foreign invoice and for one
 * that does not exist, and its ownership check fails closed — so a customer's
 * own invoice can land there when the insurance backend hiccups. None of that
 * is a permission problem the customer can act on, hence the softer wording.
 */
export const invoicePdfErrorMessageKeys: Record<number, string> = {
  403: 'document-viewer.error.temporarily-unavailable',
  404: 'document-viewer.error.no-document'
};

export function qrCodeGenerationString(invoice: Invoice, contract: PetInsuranceData) {
  let lines = [
    'SPC',
    '0200',
    '1',
    recipientIbanFormated.replace(/\s+/g, ''),
    'S',
    recipientName,
    recipientStreet,
    recipientHouseNumber,
    recipientZIP,
    recipientCity,
    recipientCountry,
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    invoice.openAmount.toFixed(2),
    'CHF',
    'S',
    contract.firstname + ' ' + contract.lastname,
    contract.street,
    contract.house_number,
    contract.zip,
    contract.city,
    contract.country,
    'QRR',
    invoice.referenceNumber.replace(/\s+/g, ''),
    '',
    'EPD'
  ]
  return lines.join('\n');
}
